import { useState, type FormEvent } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SectionCard } from '@/components/common/SectionCard';
import {
  RECOVERY_SIGN_IN_STRINGS as RS,
  SETUP_RECOVERY_GATE_STRINGS as G,
} from '@/lib/constants/two-factor-strings';

export interface SetupRecoveryGateCardProps {
  onSubmit: (recoveryCode: string) => void;
  isSubmitting: boolean;
  /** The refused attempt's reason, already worded for the user. */
  submitError?: string;
}

/**
 * Asks for a recovery code before authenticator setup, when the account
 * abandoned an authenticator re-scan (FR-221). The password alone must not
 * be enough to enrol a new app on an account that already had one.
 */
export function SetupRecoveryGateCard({ onSubmit, isSubmitting, submitError }: SetupRecoveryGateCardProps) {
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string>();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setCodeError(RS.RECOVERY_REQUIRED);
      return;
    }
    setCodeError(undefined);
    onSubmit(trimmed);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <SectionCard title={G.TITLE} subtitle={G.SUBTITLE} icon={<ShieldAlert size={20} />}>
        {submitError && (
          <p role="alert" className="text-danger mb-4 text-center text-sm">
            {submitError}
          </p>
        )}
        <div className="flex flex-col items-center gap-4 py-2">
          <Input
            label={RS.RECOVERY_LABEL}
            showLabel
            autoComplete="one-time-code"
            placeholder={RS.RECOVERY_PLACEHOLDER}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            error={codeError}
            className="max-w-xs text-center font-mono tracking-wider"
          />
          <Button type="submit" variant="navy" disabled={isSubmitting}>
            {isSubmitting ? G.SUBMITTING : G.SUBMIT}
          </Button>
          <p className="text-slate m-0 text-center text-sm">{G.NO_CODES}</p>
        </div>
      </SectionCard>
    </form>
  );
}
